import React, { useState, useEffect, useContext, useCallback } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { FaCheckCircle, FaTimesCircle, FaTrash } from 'react-icons/fa';
import { API_BASE_URL } from '../../../config/api';
import './AdminChangeRequests.css';

const AdminChangeRequests = () => {
  const { authToken } = useContext(AuthContext);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChangeRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/whitelistChangeRequests`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setChangeRequests(data);
    } catch (err) {
      setError('Error al cargar solicitudes de cambios: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchChangeRequests();
  }, [fetchChangeRequests]);

  const updateChangeRequestStatus = async (id, newStatus) => {
    if (!window.confirm(`¿Seguro que quieres cambiar el estado a ${newStatus === 'approved' ? 'Aprobado' : 'Rechazado'}?`)) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/whitelistChangeRequests/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) {
        throw new Error('Error al actualizar estado');
      }
      await fetchChangeRequests();
      alert('Solicitud de cambio actualizada.');
    } catch (err) {
      setError('Error al actualizar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChangeRequest = async (id) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta solicitud de cambio?')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/whitelistChangeRequests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error('Error al eliminar');
      }
      setChangeRequests(prev => prev.filter(r => r.id !== id));
      alert('Solicitud de cambio eliminada.');
    } catch (err) {
      setError('Error al eliminar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title">Gestión de Solicitudes de Cambios</h1>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '40px 0' }}>
            <div className="modern-spinner" />
            <span style={{ color: '#888', fontWeight: 500, fontSize: '1.1rem', marginTop: 12 }}>Cargando solicitudes...</span>
          </div>
        )}
        {error && <p className="error-message">{error}</p>}

        {!loading && !error && (
          <section className="admin-section users-list-section">
            <div className="table-responsive">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuario</th>
                    <th>Discord ID</th>
                    <th>Email de Contacto</th>
                    <th>Tipo de Cambio</th>
                    <th>Mensaje / Detalles</th>
                    <th>Estado</th>
                    <th>Fecha de Envío</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {changeRequests.length > 0 ? (
                    changeRequests.map((req) => (
                      <tr key={req.id}>
                        <td>{req.id}</td>
                        <td>{req.username}</td>
                        <td>{req.discord_id}</td>
                        <td>{req.email || <span style={{ color: '#666' }}>N/D</span>}</td>
                        <td style={{ fontWeight: 'bold', color: '#66b3ff' }}>{req.type}</td>
                        <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{req.message}</td>
                        <td>
                          <span className="status-badge" style={{
                            background: req.status === 'pending' ? 'rgba(255, 193, 7, 0.15)' : (req.status === 'approved' ? 'rgba(40, 167, 69, 0.15)' : 'rgba(220, 53, 69, 0.15)'),
                            color: req.status === 'pending' ? '#ffc107' : (req.status === 'approved' ? '#28a745' : '#ff5370'),
                            border: '1px solid',
                            borderColor: req.status === 'pending' ? 'rgba(255, 193, 7, 0.3)' : (req.status === 'approved' ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)'),
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            fontWeight: 600
                          }}>
                            {req.status === 'pending' ? 'Pendiente' : (req.status === 'approved' ? 'Aprobado' : 'Rechazado')}
                          </span>
                        </td>
                        <td>{new Date(req.created_at).toLocaleString('es-ES')}</td>
                        <td>
                          <div className="action-buttons">
                            {req.status === 'pending' && (
                              <>
                                <button onClick={() => updateChangeRequestStatus(req.id, 'approved')} className="action-button icon-button success-button" title="Aprobar"><FaCheckCircle /></button>
                                <button onClick={() => updateChangeRequestStatus(req.id, 'rejected')} className="action-button icon-button danger-button" title="Rechazar"><FaTimesCircle /></button>
                              </>
                            )}
                            <button onClick={() => handleDeleteChangeRequest(req.id)} className="action-button icon-button delete-button" title="Eliminar"><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>No hay solicitudes de cambios registradas.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminChangeRequests;
